const EARTH_RADIUS_METERS = 6371000;
const SQFT_PER_SQM = 10.76391041671;
const FEET_PER_METER = 3.2808398950131;
export const BASE_SQFT_PER_SURVIVOR = 120;

export function createLocalProjection(originCoordinate) {
  if (!Array.isArray(originCoordinate) || originCoordinate.length < 2) {
    throw new Error("createLocalProjection requires an origin coordinate.");
  }
  const [originLon, originLat] = originCoordinate;
  const metersPerLongitude = Math.cos(originLat * Math.PI / 180) * Math.PI * EARTH_RADIUS_METERS / 180;
  const metersPerLatitude = Math.PI * EARTH_RADIUS_METERS / 180;
  return {
    originCoordinate: [originLon, originLat],
    toMeters: ([lon, lat]) => [
      (lon - originLon) * metersPerLongitude,
      (lat - originLat) * metersPerLatitude,
    ],
    toCoordinate: ([x, y]) => [
      originLon + x / metersPerLongitude,
      originLat + y / metersPerLatitude,
    ],
  };
}

export function featureCoordinateRings(feature) {
  const geometry = feature?.geometry;
  if (!geometry) return [];
  if (geometry.type === "Polygon") return [geometry.coordinates];
  if (geometry.type === "MultiPolygon") return geometry.coordinates;
  return [];
}

export function featureCentroidCoordinate(feature) {
  let lon = 0;
  let lat = 0;
  let count = 0;
  for (const polygon of featureCoordinateRings(feature)) {
    for (const ring of polygon) {
      for (const coordinate of openRing(ring)) {
        lon += coordinate[0];
        lat += coordinate[1];
        count += 1;
      }
    }
  }
  return count ? [lon / count, lat / count] : null;
}

export function buildingFootprintMetrics(feature, options = {}) {
  const origin = options.originCoordinate ?? featureCentroidCoordinate(feature);
  if (!origin) return emptyMetrics();
  const projection = createLocalProjection(origin);
  const projectedPolygons = projectFeature(feature, projection);
  const floorAreaSquareMeters = projectedPolygons.reduce((sum, polygon) => {
    if (!polygon.length) return sum;
    const outer = Math.abs(ringSignedArea(polygon[0]));
    const holes = polygon.slice(1).reduce((holeSum, ring) => holeSum + Math.abs(ringSignedArea(ring)), 0);
    return sum + Math.max(0, outer - holes);
  }, 0);
  const exteriorPerimeterMeters = projectedPolygons.reduce((sum, polygon) => (
    sum + (polygon[0] ? ringPerimeter(polygon[0]) : 0)
  ), 0);
  const exposureScore = floorAreaSquareMeters > 0
    ? exteriorPerimeterMeters / Math.sqrt(floorAreaSquareMeters)
    : 0;
  return {
    floorAreaSquareMeters,
    floorAreaSquareFeet: floorAreaSquareMeters * SQFT_PER_SQM,
    exteriorPerimeterMeters,
    exteriorPerimeterFeet: exteriorPerimeterMeters * FEET_PER_METER,
    exposureScore,
    survivorCapacity: survivorCapacityFromSquareFeet(floorAreaSquareMeters * SQFT_PER_SQM),
  };
}

export function survivorCapacityFromSquareFeet(squareFeet, squareFeetPerSurvivor = BASE_SQFT_PER_SURVIVOR) {
  if (!Number.isFinite(squareFeet) || squareFeet <= 0) return 0;
  return Math.max(1, Math.floor(squareFeet / squareFeetPerSurvivor));
}

export function fitBuildingGrid(feature, options = {}) {
  const cellSizeMeters = positiveNumber(options.cellSizeMeters, 3);
  const angleStepDegrees = positiveNumber(options.angleStepDegrees, 2);
  const nominalCellSizeMeters = positiveNumber(options.nominalCellSizeMeters, cellSizeMeters);
  const shrinkPercent = Math.max(0, Math.min(1, 1 - cellSizeMeters / nominalCellSizeMeters));
  const origin = options.originCoordinate ?? featureCentroidCoordinate(feature);
  if (!origin) return null;
  const projection = createLocalProjection(origin);
  const projectedPolygons = projectFeature(feature, projection);
  const points = projectedPolygons.flatMap((polygon) => polygon[0] ?? []);
  if (points.length < 3) return null;

  const candidates = [];
  if (Number.isFinite(options.angleDegrees)) {
    candidates.push(normalizeHalfTurn(options.angleDegrees));
  } else {
    for (let angle = 0; angle < 180; angle += angleStepDegrees) {
      candidates.push(normalizeHalfTurn(angle));
    }
  }

  let best = null;
  for (const angleDegrees of candidates) {
    const candidate = buildGridCandidate(projectedPolygons, points, angleDegrees, cellSizeMeters, {
      fixedCols: options.fixedCols,
      fixedRows: options.fixedRows,
      nominalCellSizeMeters,
      shrinkPercent,
    });
    if (!best || candidate.score > best.score) best = candidate;
  }
  return best ? {
    ...best,
    originCoordinate: origin,
    cellSizeMeters,
    nominalCellSizeMeters,
    shrinkPercent,
    angleStepDegrees,
  } : null;
}

export function fitBestBuildingGrid(feature, options = {}) {
  const cellSizesMeters = Array.isArray(options.cellSizesMeters) && options.cellSizesMeters.length
    ? options.cellSizesMeters.map((value) => positiveNumber(value, 0)).filter((value) => value > 0)
    : [2, 2.5, 3];
  const maxShrinkPercent = Math.max(0, Math.min(0.75, Number.isFinite(Number(options.maxShrinkPercent)) ? Number(options.maxShrinkPercent) : 0.25));
  const shrinkStepPercent = Math.max(0.01, Math.min(maxShrinkPercent || 0.01, Number.isFinite(Number(options.shrinkStepPercent)) ? Number(options.shrinkStepPercent) : 0.025));
  const extraShrinkPercent = Math.max(0, Math.min(0.25, Number.isFinite(Number(options.extraShrinkPercent)) ? Number(options.extraShrinkPercent) : 0));
  let best = null;
  for (const nominalCellSizeMeters of cellSizesMeters) {
    for (let shrinkPercent = 0; shrinkPercent <= maxShrinkPercent + 1e-9; shrinkPercent += shrinkStepPercent) {
      const cellSizeMeters = nominalCellSizeMeters * (1 - Math.min(maxShrinkPercent, shrinkPercent));
      const candidate = fitBuildingGrid(feature, {
        ...options,
        cellSizeMeters,
        nominalCellSizeMeters,
      });
      if (!candidate?.insideCells) continue;
      if (!best || candidate.score > best.score) best = candidate;
    }
  }
  if (!best || extraShrinkPercent <= 0) return best;
  return fitBuildingGrid(feature, {
    ...options,
    angleDegrees: best.angleDegrees,
    cellSizeMeters: best.cellSizeMeters * (1 - extraShrinkPercent),
    fixedCols: best.cols,
    fixedRows: best.rows,
    nominalCellSizeMeters: best.nominalCellSizeMeters,
    originCoordinate: best.originCoordinate,
  });
}

export function gridConfigFromFit(fit) {
  if (!fit) return null;
  const config = {
    originCoordinate: [...fit.originCoordinate],
    nominalCellSizeMeters: fit.nominalCellSizeMeters,
    cellSizeMeters: fit.cellSizeMeters,
    shrinkPercent: fit.shrinkPercent,
    angleDegrees: fit.angleDegrees,
    angleStepDegrees: fit.angleStepDegrees,
    cols: fit.cols,
    rows: fit.rows,
    insideCells: fit.insideCells,
    coverage: fit.coverage,
  };
  if (Number.isFinite(Number(fit.version))) config.version = Number(fit.version);
  return config;
}

export function fitBuildingGridFromConfig(feature, grid) {
  if (!grid) return null;
  return fitBuildingGrid(feature, {
    originCoordinate: grid.originCoordinate,
    cellSizeMeters: grid.cellSizeMeters,
    fixedCols: grid.cols,
    fixedRows: grid.rows,
    nominalCellSizeMeters: grid.nominalCellSizeMeters,
    angleDegrees: grid.angleDegrees,
    angleStepDegrees: grid.angleStepDegrees,
  });
}

export function gridCellsToGeoJSON(fit) {
  const projection = fit?.originCoordinate ? createLocalProjection(fit.originCoordinate) : null;
  if (!projection) return featureCollection([]);
  return featureCollection(fit.cells.filter((cell) => cell.inside).map((cell) => ({
    type: "Feature",
    properties: { row: cell.row, col: cell.col },
    geometry: {
      type: "Polygon",
      coordinates: [[...cell.corners.map((point) => projection.toCoordinate(point)), projection.toCoordinate(cell.corners[0])]],
    },
  })));
}

// ---- Snap-and-fill base shape generator -----------------------------------
// Forks a real building footprint into a NEW cell shape whose silhouette reads
// as the same building, judged by corner count (not area overlap). Pipeline:
// dominant-edge angle -> simplify to essential corners -> snap corners to the
// grid lattice -> fill cells whose center is inside -> keep the largest blob.
// Snapping auto-rectilinearises near-axis (88/92 deg) walls. Interactive
// derivation lives in prototype/base-geometry-lab.html.
export function generateBaseShape(feature, options = {}) {
  const cellSizeMeters = positiveNumber(options.cellSizeMeters, 2);
  const simplifyToleranceMeters = Number.isFinite(Number(options.simplifyToleranceMeters))
    ? Number(options.simplifyToleranceMeters) : 2;
  const origin = options.originCoordinate ?? featureCentroidCoordinate(feature);
  if (!origin) return null;
  const projection = createLocalProjection(origin);
  const projected = projectFeature(feature, projection);
  if (!projected.length || !projected[0]?.[0] || projected[0][0].length < 3) return null;

  const angleDegrees = dominantAngle(projected);
  const rotated = projected.map((polygon) => polygon.map((ring) => ring.map((point) => rotatePoint(point, -angleDegrees))));
  const simplified = simplifyToleranceMeters > 0
    ? rotated.map((polygon) => polygon.map((ring) => simplifyRing(ring, simplifyToleranceMeters)))
    : rotated;

  const points = simplified.flatMap((polygon) => polygon[0] ?? []);
  if (points.length < 3) return null;
  const bounds = boundsForPoints(points);
  const cols = Math.max(1, Math.ceil(bounds.width / cellSizeMeters)) + 1;
  const rows = Math.max(1, Math.ceil(bounds.height / cellSizeMeters)) + 1;

  const latticePolygons = simplified
    .map((polygon) => polygon.map((ring) => snapRing(ring, bounds.minX, bounds.minY, cellSizeMeters)).filter((ring) => ring.length >= 3))
    .filter((polygon) => polygon.length);

  const filled = new Set();
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      if (pointInLatticePolygons(col + 0.5, row + 0.5, latticePolygons)) filled.add(`${row}:${col}`);
    }
  }
  const cells = largestCellComponent(filled);
  if (!cells.size) return null;

  return {
    origin,
    angleDegrees,
    cellSizeMeters,
    minX: bounds.minX,
    minY: bounds.minY,
    cols,
    rows,
    cells,
    corners: polyominoCorners(cells),
  };
}

export function baseShapeCellsToGeoJSON(shape) {
  if (!shape) return featureCollection([]);
  const projection = createLocalProjection(shape.origin);
  const toWorld = (col, row) => projection.toCoordinate(rotatePoint(
    [shape.minX + col * shape.cellSizeMeters, shape.minY + row * shape.cellSizeMeters], shape.angleDegrees));
  const features = [];
  for (const key of shape.cells) {
    const [row, col] = key.split(":").map(Number);
    const ring = [toWorld(col, row), toWorld(col + 1, row), toWorld(col + 1, row + 1), toWorld(col, row + 1)];
    features.push({ type: "Feature", properties: { row, col }, geometry: { type: "Polygon", coordinates: [[...ring, ring[0]]] } });
  }
  return featureCollection(features);
}

export function baseShapeOutlineToGeoJSON(shape) {
  if (!shape) return featureCollection([]);
  const projection = createLocalProjection(shape.origin);
  const toWorld = ([col, row]) => projection.toCoordinate(rotatePoint(
    [shape.minX + col * shape.cellSizeMeters, shape.minY + row * shape.cellSizeMeters], shape.angleDegrees));
  const rings = traceCellUnion(shape.cells);
  if (!rings.length) return featureCollection([]);
  rings.sort((a, b) => Math.abs(ringSignedArea(b)) - Math.abs(ringSignedArea(a)));
  const coordinates = rings.map((ring) => { const world = ring.map(toWorld); return [...world, world[0]]; });
  return featureCollection([{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates } }]);
}

// corners of a polyomino outline (O(cells)): per lattice point, 1-or-3 of the 4
// surrounding cells inside = a corner, diagonal-2 = a pinch (two corners).
export function polyominoCorners(cells) {
  if (!cells.size) return 0;
  let rMin = Infinity, rMax = -Infinity, cMin = Infinity, cMax = -Infinity;
  for (const key of cells) {
    const [row, col] = key.split(":").map(Number);
    rMin = Math.min(rMin, row); rMax = Math.max(rMax, row);
    cMin = Math.min(cMin, col); cMax = Math.max(cMax, col);
  }
  const inside = (row, col) => cells.has(`${row}:${col}`);
  let corners = 0;
  for (let lr = rMin; lr <= rMax + 1; lr += 1) {
    for (let lc = cMin; lc <= cMax + 1; lc += 1) {
      const q1 = inside(lr - 1, lc - 1), q2 = inside(lr - 1, lc), q3 = inside(lr, lc - 1), q4 = inside(lr, lc);
      const count = q1 + q2 + q3 + q4;
      if (count === 1 || count === 3) corners += 1;
      else if (count === 2 && ((q1 && q4) || (q2 && q3))) corners += 2;
    }
  }
  return corners;
}

// length-weighted dominant wall orientation (deg, in [-45,45]); the double-angle
// x4 trick makes perpendicular walls reinforce instead of cancel (period 90 deg).
function dominantAngle(projectedPolygons) {
  let vx = 0, vy = 0;
  for (const polygon of projectedPolygons) {
    const ring = polygon[0];
    if (!ring) continue;
    for (let i = 0; i < ring.length; i += 1) {
      const a = ring[i], b = ring[(i + 1) % ring.length];
      const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy);
      if (len < 1e-9) continue;
      const theta = Math.atan2(dy, dx) * 4;
      vx += len * Math.cos(theta); vy += len * Math.sin(theta);
    }
  }
  if (vx === 0 && vy === 0) return 0;
  return Math.atan2(vy, vx) / 4 * 180 / Math.PI;
}

// vertex-decimation: drop the least-significant vertex until all deviate > tol.
function simplifyRing(ring, toleranceMeters) {
  const points = ring.map((point) => [point[0], point[1]]);
  while (points.length > 4) {
    let bestIndex = -1, bestDistance = Infinity;
    for (let i = 0; i < points.length; i += 1) {
      const distance = perpendicularDistance(points[i], points[(i - 1 + points.length) % points.length], points[(i + 1) % points.length]);
      if (distance < bestDistance) { bestDistance = distance; bestIndex = i; }
    }
    if (bestDistance >= toleranceMeters) break;
    points.splice(bestIndex, 1);
  }
  return points;
}

function perpendicularDistance(point, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy);
  if (len < 1e-9) return Math.hypot(point[0] - a[0], point[1] - a[1]);
  return Math.abs(dy * (point[0] - a[0]) - dx * (point[1] - a[1])) / len;
}

// snap ring vertices to the integer lattice, then drop duplicate + collinear ones.
function snapRing(ring, minX, minY, cellSizeMeters) {
  let points = ring.map(([x, y]) => [Math.round((x - minX) / cellSizeMeters), Math.round((y - minY) / cellSizeMeters)]);
  points = points.filter((point, i) => {
    const prev = points[(i - 1 + points.length) % points.length];
    return !(point[0] === prev[0] && point[1] === prev[1]);
  });
  let changed = true;
  while (changed && points.length > 3) {
    changed = false;
    for (let i = 0; i < points.length; i += 1) {
      const a = points[(i - 1 + points.length) % points.length];
      const b = points[i];
      const c = points[(i + 1) % points.length];
      if ((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]) === 0) { points.splice(i, 1); changed = true; break; }
    }
  }
  return points;
}

function pointInLatticePolygons(x, y, polygons) {
  for (const polygon of polygons) {
    if (!polygon[0] || !pointInRing([x, y], polygon[0])) continue;
    if (polygon.slice(1).some((hole) => pointInRing([x, y], hole))) continue;
    return true;
  }
  return false;
}

function largestCellComponent(cellKeys) {
  const seen = new Set();
  let best = new Set();
  for (const start of cellKeys) {
    if (seen.has(start)) continue;
    const component = new Set([start]);
    const stack = [start];
    seen.add(start);
    while (stack.length) {
      const [row, col] = stack.pop().split(":").map(Number);
      for (const [nr, nc] of [[row + 1, col], [row - 1, col], [row, col + 1], [row, col - 1]]) {
        const key = `${nr}:${nc}`;
        if (!cellKeys.has(key) || seen.has(key)) continue;
        seen.add(key); component.add(key); stack.push(key);
      }
    }
    if (component.size > best.size) best = component;
  }
  return best;
}

// trace the polyomino outline into rings of [col,row] lattice points (CW cells;
// interior edges cancel, leaving the outer ring + any hole rings).
function traceCellUnion(cells) {
  const has = (row, col) => cells.has(`${row}:${col}`);
  const outgoing = new Map();
  const push = (from, to) => { if (!outgoing.has(from)) outgoing.set(from, []); outgoing.get(from).push(to); };
  for (const key of cells) {
    const [row, col] = key.split(":").map(Number);
    if (!has(row - 1, col)) push(`${col},${row}`, `${col + 1},${row}`);
    if (!has(row, col + 1)) push(`${col + 1},${row}`, `${col + 1},${row + 1}`);
    if (!has(row + 1, col)) push(`${col + 1},${row + 1}`, `${col},${row + 1}`);
    if (!has(row, col - 1)) push(`${col},${row + 1}`, `${col},${row}`);
  }
  const rings = [];
  for (const startKey of outgoing.keys()) {
    while ((outgoing.get(startKey) ?? []).length) {
      const ring = [];
      let current = startKey;
      do {
        const nexts = outgoing.get(current);
        if (!nexts || !nexts.length) break;
        const next = nexts.pop();
        ring.push(current.split(",").map(Number));
        current = next;
      } while (current !== startKey);
      if (ring.length >= 3) rings.push(ring);
    }
  }
  return rings;
}

function buildGridCandidate(projectedPolygons, points, angleDegrees, cellSizeMeters, options = {}) {
  const rotatedPoints = points.map((point) => rotatePoint(point, -angleDegrees));
  const bounds = boundsForPoints(rotatedPoints);
  const rawCols = Math.max(1, bounds.width / cellSizeMeters);
  const rawRows = Math.max(1, bounds.height / cellSizeMeters);
  const cols = Math.max(1, Number.isFinite(Number(options.fixedCols)) ? Math.round(Number(options.fixedCols)) : Math.round(rawCols));
  const rows = Math.max(1, Number.isFinite(Number(options.fixedRows)) ? Math.round(Number(options.fixedRows)) : Math.round(rawRows));
  const width = cols * cellSizeMeters;
  const height = rows * cellSizeMeters;
  const minX = (bounds.minX + bounds.maxX) / 2 - width / 2;
  const minY = (bounds.minY + bounds.maxY) / 2 - height / 2;
  const cells = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const centerRotated = [minX + (col + 0.5) * cellSizeMeters, minY + (row + 0.5) * cellSizeMeters];
      const center = rotatePoint(centerRotated, angleDegrees);
      const corners = [
        [minX + col * cellSizeMeters, minY + row * cellSizeMeters],
        [minX + (col + 1) * cellSizeMeters, minY + row * cellSizeMeters],
        [minX + (col + 1) * cellSizeMeters, minY + (row + 1) * cellSizeMeters],
        [minX + col * cellSizeMeters, minY + (row + 1) * cellSizeMeters],
      ].map((point) => rotatePoint(point, angleDegrees));
      const inside = cellFullyInsideFeature(corners, projectedPolygons);
      cells.push({ row, col, center, corners, inside });
    }
  }
  const insideCells = cells.filter((cell) => cell.inside).length;
  const buildingArea = projectedPolygons.reduce((sum, polygon) => sum + Math.abs(ringSignedArea(polygon[0] ?? [])), 0);
  const insideArea = insideCells * cellSizeMeters * cellSizeMeters;
  const coverage = buildingArea > 0 ? Math.min(1, insideArea / buildingArea) : 0;
  const waste = cells.length ? 1 - insideCells / cells.length : 1;
  const rectangularity = cells.length ? insideCells / cells.length : 0;
  const aspectPenalty = Math.abs(Math.log(Math.max(cols, 1) / Math.max(rows, 1))) * 16;
  const shrinkPenalty = (options.shrinkPercent ?? 0) * 120;
  const score = coverage * 1000
    + rectangularity * 220
    - waste * 70
    - aspectPenalty
    - shrinkPenalty
    - Math.abs(rawCols - cols)
    - Math.abs(rawRows - rows);
  return {
    angleDegrees,
    nominalCellSizeMeters: options.nominalCellSizeMeters ?? cellSizeMeters,
    shrinkPercent: options.shrinkPercent ?? 0,
    cols,
    rows,
    widthMeters: width,
    heightMeters: height,
    cells,
    insideCells,
    totalCells: cells.length,
    coverage,
    waste,
    rectangularity,
    score,
    projectedPolygons,
    rotatedBounds: { minX, minY, maxX: minX + width, maxY: minY + height },
  };
}

function featureCollection(features) {
  return { type: "FeatureCollection", features };
}

function projectFeature(feature, projection) {
  return featureCoordinateRings(feature).map((polygon) => (
    polygon.map((ring) => openRing(ring).map((coordinate) => projection.toMeters(coordinate)))
  ));
}

function openRing(ring) {
  if (!Array.isArray(ring)) return [];
  if (ring.length > 1) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) return ring.slice(0, -1);
  }
  return [...ring];
}

function ringSignedArea(points) {
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    area += current[0] * next[1] - next[0] * current[1];
  }
  return area / 2;
}

function ringPerimeter(points) {
  let perimeter = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    perimeter += Math.hypot(next[0] - current[0], next[1] - current[1]);
  }
  return perimeter;
}

function pointInProjectedFeature(point, projectedPolygons) {
  for (const polygon of projectedPolygons) {
    const outer = polygon[0];
    if (!outer || !pointInRing(point, outer)) continue;
    const inHole = polygon.slice(1).some((ring) => pointInRing(point, ring));
    if (!inHole) return true;
  }
  return false;
}

function cellFullyInsideFeature(corners, projectedPolygons) {
  if (!corners.every((corner) => pointInProjectedFeature(corner, projectedPolygons))) return false;
  for (const edge of ringEdges(corners)) {
    for (const polygon of projectedPolygons) {
      for (const ring of polygon) {
        for (const boundary of ringEdges(ring)) {
          if (segmentsIntersect(edge[0], edge[1], boundary[0], boundary[1])) return false;
        }
      }
    }
  }
  for (const polygon of projectedPolygons) {
    for (const hole of polygon.slice(1)) {
      if (hole.some((point) => pointInConvexRing(point, corners))) return false;
    }
  }
  return true;
}

function pointInRing(point, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersects = (yi > point[1]) !== (yj > point[1])
      && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function pointInConvexRing(point, ring) {
  return pointInRing(point, ring);
}

function ringEdges(ring) {
  const edges = [];
  for (let index = 0; index < ring.length; index += 1) {
    edges.push([ring[index], ring[(index + 1) % ring.length]]);
  }
  return edges;
}

function segmentsIntersect(p1, p2, p3, p4) {
  const o1 = orientation(p1, p2, p3);
  const o2 = orientation(p1, p2, p4);
  const o3 = orientation(p3, p4, p1);
  const o4 = orientation(p3, p4, p2);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(p1, p3, p2)) return true;
  if (o2 === 0 && onSegment(p1, p4, p2)) return true;
  if (o3 === 0 && onSegment(p3, p1, p4)) return true;
  if (o4 === 0 && onSegment(p3, p2, p4)) return true;
  return false;
}

function orientation(a, b, c) {
  const value = (b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1]);
  if (Math.abs(value) < 1e-9) return 0;
  return value > 0 ? 1 : 2;
}

function onSegment(a, b, c) {
  return Math.min(a[0], c[0]) <= b[0] && b[0] <= Math.max(a[0], c[0])
    && Math.min(a[1], c[1]) <= b[1] && b[1] <= Math.max(a[1], c[1]);
}

export function rotatePoint([x, y], degrees) {
  const radians = degrees * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return [x * cos - y * sin, x * sin + y * cos];
}

function boundsForPoints(points) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of points) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

function positiveNumber(value, fallback) {
  return Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : fallback;
}

function normalizeHalfTurn(degrees) {
  return ((degrees % 180) + 180) % 180;
}

function emptyMetrics() {
  return {
    floorAreaSquareMeters: 0,
    floorAreaSquareFeet: 0,
    exteriorPerimeterMeters: 0,
    exteriorPerimeterFeet: 0,
    exposureScore: 0,
    survivorCapacity: 0,
  };
}
